import type * as React from "react";
import { memo } from "react";
import classNames from "classnames";
import ClearCartButton from "~/features/cart/activeQuote/components/productTable/ClearCartButton";
import CartProductTableHeaderCell from "~/features/cart/activeQuote/components/productTable/CartProductTableHeaderCell";
import ProductEntry from "~/features/cart/activeQuote/components/productTable/ProductEntry";
import SalesmanDetailsToggle from "~/features/productDetails/components/AddToCartCard/SalesmanAddToCartCard/SalesmanDetailsToggle";
import { Stack } from "~/common/mui";
import Spacer from "~/common/components/Spacer";
import { useTranslation } from "react-i18next";
import type { QuoteTableViewModel } from "~/features/cart/activeQuote/models/QuoteTableViewModel";

interface ProductTableProps {
  className?: string;
  tableViewModel: QuoteTableViewModel;
  showInternal?: boolean;
  onClearCart?: () => void;
  onToggleDetails?: (newVal: boolean) => void;
  "data-test-id"?: string;
}

const ProductTable: React.FC<ProductTableProps> = (props) => {
  const { tableViewModel, showInternal, "data-test-id": testId } = props;
  const editMode = tableViewModel.mode === "edit";
  const { t } = useTranslation();
  const cls = classNames(
    props.className,
    "scha__active-quote__product-table scha__data-table",
    showInternal && "scha__active-quote__product-table--show-details",
  );

  const headerTitle = (header: string) => t(`activeQuotePage.table.header.${header}.title`);
  const headerSubtitle = (header: string) => t(`activeQuotePage.table.header.${header}.subtitle`);

  const isEmpty = tableViewModel.entries.length === 0;

  return (
    <>
      <div className={cls} data-test-id={testId}>
        <div className="scha__active-quote__product-table__header">
          <CartProductTableHeaderCell title={headerTitle("product")} first={true} />
          <CartProductTableHeaderCell title={headerTitle("quantity")} firstOnRightSide={true} />
          <CartProductTableHeaderCell title={headerTitle("packaging")} />
          <CartProductTableHeaderCell title={headerTitle("delivery")} />
          <CartProductTableHeaderCell title={headerTitle("offerPrice")} />
          <CartProductTableHeaderCell title={headerTitle("total")} />
          {showInternal && (
            <>
              <CartProductTableHeaderCell internal={true} title={headerTitle("sellingPrice")} />
              <CartProductTableHeaderCell internal={true} title={headerTitle("targetPrice")} />
              <CartProductTableHeaderCell internal={true} title={headerTitle("targetTotal")} />
            </>
          )}
          <CartProductTableHeaderCell
            lastOnRightSide={true}
            alignEnd={true}
            title={
              <Stack gap={2}>
                {props.onToggleDetails && (
                  <SalesmanDetailsToggle
                    data-test-id={"active-quote-toggle-details"}
                    checked={showInternal}
                    onCheck={props.onToggleDetails}
                  />
                )}
                {props.onClearCart && <ClearCartButton onClick={props.onClearCart} disabled={isEmpty} />}
              </Stack>
            }
          />
        </div>
        <Spacer className={"my-0 p-relative"} isThick={true} />
        <div className={"scha__active-quote__product-table__body"}>
          {!isEmpty &&
            tableViewModel.entries.map((entryViewModel, index) => (
              <ProductEntry key={entryViewModel.cartEntryId} index={index} entryViewModel={entryViewModel} />
            ))}
          {isEmpty && (
            <div style={{ margin: "80px 0" }} className={"f-center"}>
              <p className={"display-small m-0"}>{t("activeQuotePage.noItems.upper")}</p>
              <p className={"title-medium f-carbon mb-3 pb-2 mt-1"}>{t("activeQuotePage.noItems.lower")}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default memo(ProductTable);
